import { animate, style, transition, trigger } from '@angular/animations';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, Subject, Subscription, combineLatest, of } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { AppService } from 'src/app/app.service';
import { GameApi } from 'src/app/shared/apis/game.api';
import { GameListItem, GameSearch, NewGameList } from 'src/app/shared/interfaces/game.interface';
import { LayoutService } from 'src/app/shared/service/layout.service';
import { LocalStorageService } from 'src/app/shared/service/localstorage.service';
import { MiniGameService } from '../minigame.service';

@UntilDestroy()
@Component({
  selector: 'app-new-search-game',
  templateUrl: './new-search-game.component.html',
  styleUrls: ['./new-search-game.component.scss'],
  animations: [
    trigger('enter', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(20px)' }),
        animate('0.2s ease-in-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
    ]),
  ],
})
export class NewSearchGameComponent implements OnInit, OnChanges {
  constructor(
    private overlay: Overlay,
    private vcRef: ViewContainerRef,
    public appService: AppService,
    private gameApi: GameApi,
    private router: Router,
    private layout: LayoutService,
    private localStorageService: LocalStorageService,
    private miniGameService: MiniGameService,
    private route: ActivatedRoute,
  ) {}
  domain = window.location.origin;
  @Input() labelCodes: string[] = [];
  @ViewChild('overlayTemplate') overlayTemplate!: TemplateRef<unknown>;

  labelCodesChange$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  active$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  overlayRef?: OverlayRef | null;

  search$: Subject<string> = new Subject();
  searchValue: string = '';
  realSearchValue: string = '';
  oldSearchValue: string = '';
  searchResult: GameSearch | null = null;
  loading!: boolean;

  recommenGames?: NewGameList;
  searchHistory: string[] = [];

  get resultLength() {
    if (!this.searchResult) return 0;
    return this.searchResult.gameInfo.length + this.searchResult.labelInfo.length;
  }

  updatedFavoriteIds = this.miniGameService.allFavoriteIds$.pipe(untilDestroyed(this)).subscribe(ids => {
    if (!this.searchResult?.gameInfo) return;
    this.searchResult.gameInfo.forEach((item: GameListItem) => {
      item.isFavorite = ids.includes(item.id);
    });
  });

  ngOnInit() {
    this.updateSearchHistory();

    this.search$
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        switchMap(v => this.gameApi.getGameByName(v)),
        map(x => x?.data || null),
      )
      .subscribe(data => {
        this.loading = false;
        if (data) {
          if (data.gameInfo.length > 0 || data.labelInfo.length > 0) {
            this.updateSearchHistory(this.realSearchValue);
            this.searchResult = data;
          }
        }
      });

    combineLatest([
      this.route.paramMap ?? of(null),
      this.labelCodesChange$.pipe(
        filter(v => v),
        map(() => this.labelCodes),
      ),
      this.active$.pipe(filter(v => v)),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([param, labelCodes]) => {
        const label = param.get('label');
        if (label) {
          this.getRecommenGames([label]);
        } else if (labelCodes.length > 0) {
          this.getRecommenGames(labelCodes, true);
        } else {
          // 没有任何偏向，默认请求全部
          this.getRecommenGames([]);
        }
      });

    this.layout.isH5$.pipe(untilDestroyed(this)).subscribe(() => {
      this.overlayRef?.updateScrollStrategy(this.overlay.scrollStrategies.block());
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.labelCodes && changes.labelCodes.currentValue.length > 0) {
      this.labelCodesChange$.next(true);
    }
  }

  /**显示搜索主体内容 */
  showOverlay() {
    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(document.querySelector('.layout-main-content') as Element)
        .withPositions([
          {
            originX: 'center',
            originY: 'top',
            overlayX: 'center',
            overlayY: 'top',
          },
        ])
        .withFlexibleDimensions(true),
    });
    this.overlayRef.backdropClick().subscribe(() => this.close());
    this.overlayRef.attach(new TemplatePortal(this.overlayTemplate, this.vcRef));
    this.searchValue = this.realSearchValue = '';
    this.active$.next(true);
  }

  breakpoint = 0;
  breakpoint$?: Subscription;
  /**初始化搜索结果显示的断点 */
  initBreakpoint(on: boolean, el: HTMLDivElement) {
    if (on) {
      this.breakpoint$ = this.layout
        .getBreakpoint(el)
        .pipe(untilDestroyed(this))
        .subscribe(x => {
          this.breakpoint = x as number;
        });
    } else {
      this.breakpoint$?.unsubscribe();
    }
  }

  /**输入的搜索值变化 */
  searchValueChange() {
    this.realSearchValue = this.searchValue.trim();
    if (!this.realSearchValue) return;
    if (this.realSearchValue === this.oldSearchValue) return;
    this.oldSearchValue = this.realSearchValue;
    this.searchResult = null;
    this.loading = true;
    this.search$.next(this.realSearchValue);
  }

  /**
   * 获取、更新、删除历史记录
   * bc最大20条,只有成功有结果的搜索关键词才会被记录
   * 重新使用已有的关键词会更新该关键词位置到最前
   *
   * @param newVal 新增
   * @param deleteIndex 删除的索引
   */
  updateSearchHistory(newVal?: string, deleteIndex?: number) {
    if (newVal) {
      const index = this.searchHistory.findIndex(x => x === newVal);
      if (index >= 0) {
        this.searchHistory.splice(index, 1);
        this.searchHistory = [newVal, ...this.searchHistory];
      } else {
        this.searchHistory = [newVal, ...this.searchHistory.slice(0, 19)];
      }
      this.localStorageService.searchHistory = this.searchHistory;
    } else if (deleteIndex !== undefined) {
      this.searchHistory.splice(deleteIndex, 1);
      this.localStorageService.searchHistory = this.searchHistory;
    } else {
      this.searchHistory = this.localStorageService.searchHistory;
    }
  }

  /**清空搜索历史 */
  clearHistory() {
    this.searchHistory = [];
    this.localStorageService.searchHistory = this.searchHistory;
  }

  /**
   * 通过label 值获取 游戏
   *
   * @param label code
   * @param isHill 是否是 大厅
   */
  getRecommenGames(label: string[], isHill: boolean = false) {
    this.miniGameService
      .getGameListByMultipleLabel(label, false, 25, isHill)
      .pipe(untilDestroyed(this))
      .subscribe(data => {
        this.recommenGames = data;
      });
  }

  /**关闭搜索 */
  close() {
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }

  /**点击搜索结果里的标签 */
  clicklabelItem(labelId: string) {
    this.router.navigateByUrl(`${this.appService.languageCode}/casino/category/${labelId}`);
    this.close();
  }
}
